import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { AppReducerStateProps, store } from "../../lib/store";
import Img from "react-cloudimage-responsive";
import paymentV2NotifyWithdraw, {
  NotifyWithdrawCreditRequest,
  NotifyWithdrawCreditResponse,
} from "lib/worker/api/payment_v2_notify_withdraw_credit";
import { BankByMember } from "lib/worker/api/getinfo";
import API from "lib/worker/api/getinfo";

// Helper functions for external use
import {
  alertWithWithdrawV2,
  alertWithWithdrawV2Close,
} from "./withdraw_modal_v2_helper";
import { alertWithMessage } from "./message_modal";
export { alertWithWithdrawV2, alertWithWithdrawV2Close };

// Compatibility function (can replace alertWithWithDraw)
export const alertWithWithDraw = alertWithWithdrawV2;

interface WithdrawModalV2Props {
  state: AppReducerStateProps;
}

const WithdrawModalV2: React.FC<WithdrawModalV2Props> = (props) => {
  const isWithdrawV2Modal = props.state.member.isWithdrawV2Modal;

  const closeModal = () => {
    store.dispatch({
      type: "ACTION_WITHDRAW_V2_IS_CLOSE_MODAL",
    });
  };
  const { getinfo } = props.state.member;
  const { Banks, Balance, CurrencyName } = getinfo;

  // States
  const [step, setStep] = useState<number>(1);
  const [selectedBank, setSelectedBank] = useState<BankByMember | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [withdrawResult, setWithdrawResult] =
    useState<NotifyWithdrawCreditResponse | null>(null);
  const maxWithdrawAmountInteger = Math.max(Math.floor(Balance || 0), 0);

  // Step titles
  const stepTitles: { [key: number]: string } = {
    1: "เลือกธนาคาร",
    2: "ระบุจำนวนเงิน",
    3: "ยืนยันการถอนเงิน",
  };

  // Reset modal when closed
  useEffect(() => {
    if (!isWithdrawV2Modal) {
      setStep(1);
      setSelectedBank(null);
      setAmount("");
      setWithdrawResult(null);
      setLoading(false);
    }
  }, [isWithdrawV2Modal]);

  // Handle next button
  const handleNext = () => {
    if (step === 1 && selectedBank) {
      setStep(2);
    } else if (step === 2) {
      handleNotifyWithdraw();
    } else if (step === 3) {
      // Reset and start new transaction
      setStep(1);
      setSelectedBank(null);
      setAmount("");
      setWithdrawResult(null);
    }
  };

  // Handle withdraw notification
  const handleNotifyWithdraw = async () => {
    try {
      setLoading(true);

      if (!selectedBank?.ID || !amount) {
        throw new Error("กรุณาเลือกธนาคารและระบุจำนวนเงิน");
      }

      const withdrawAmount = parseInt(amount, 10);

      if (!Number.isInteger(withdrawAmount) || withdrawAmount <= 0) {
        throw new Error("กรุณาระบุจำนวนเงินแบบจำนวนเต็มเท่านั้น");
      }

      if (withdrawAmount > maxWithdrawAmountInteger) {
        throw new Error("จำนวนเงินเกินยอดคงเหลือ");
      }

      const withdrawRequest: NotifyWithdrawCreditRequest = {
        bank_member_id: selectedBank.ID,
        amount: withdrawAmount,
        notes: `ถอนเงิน ${withdrawAmount} ${CurrencyName || "THB"} เข้าบัญชี ${
          selectedBank.Type
        } ${selectedBank.BankNumber}`,
      };

      const result = await paymentV2NotifyWithdraw(withdrawRequest);

      if (result.success) {
        setWithdrawResult(result);
        setStep(3);

        // Update balance after successful withdrawal
        try {
          await API();
        } catch (error) {
          console.error("Failed to update balance:", error);
        }
      } else {
        throw new Error(result.message || "ไม่สามารถแจ้งถอนเงินได้");
      }
    } catch (error: any) {
      console.error("Withdraw error:", error);
      alertWithMessage(error?.response?.data?.message || error?.message || "เกิดข้อผิดพลาดในการแจ้งถอนเงิน");
    } finally {
      setLoading(false);
    }
  };

  // Button text
  const getButtonText = () => {
    if (step === 1) return "ถัดไป";
    if (step === 2) return loading ? "กำลังดำเนินการ..." : "แจ้งถอนเงิน";
    if (step === 3) return "ทำรายการใหม่";
    return "ถัดไป";
  };

  // Button disabled state
  const isButtonDisabled = () => {
    if (step === 1) return !selectedBank || loading;
    if (step === 2) {
      const inputAmount = parseInt(amount, 10);
      return (
        !amount ||
        !Number.isInteger(inputAmount) ||
        inputAmount <= 0 ||
        inputAmount > maxWithdrawAmountInteger ||
        loading
      );
    }
    return loading;
  };

  const handleAmountInputChange = (value: string) => {
    const normalizedValue = value.replace(/,/g, "").trim();

    if (!normalizedValue) {
      setAmount("");
      return;
    }

    if (!/^\d*\.?\d*$/.test(normalizedValue)) {
      return;
    }

    const [wholeNumber] = normalizedValue.split(".");
    setAmount(wholeNumber.replace(/^0+(?=\d)/, ""));
  };
  // Handle amount selection
  const handleAmountSelect = (selectedAmount: number) => {
    const integerAmount = Math.max(Math.floor(selectedAmount), 0);
    setAmount(integerAmount.toString());
  };

  // Format balance
  const formatBalance = () => {
    return Balance?.toLocaleString() || "0";
  };

  // Step 1: Bank Selection
  const renderStep1 = () => (
    <div className="space-y-4">
      {/* Balance Display */}
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-4">
        <div className="text-center">
          <p className="text-red-700 text-sm mb-1">{"ยอดเงินคงเหลือ"}</p>
          <p className="text-red-800 text-2xl font-bold">
            {CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
            {formatBalance()}
          </p>
        </div>
      </div>

      <label className="block text-gray-900 font-bold text-sm sm:text-base">
        {"เลือกบัญชีธนาคารที่ต้องการถอนเงิน"}
      </label>

      <div className="space-y-3">
        {Banks?.map((bank: BankByMember) => (
          <div
            key={bank.ID}
            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
              selectedBank?.ID === bank.ID
                ? "border-red-500 bg-red-50 shadow-md transform scale-[1.02]"
                : "border-gray-200 bg-white hover:border-red-300 hover:bg-red-25 shadow-sm"
            }`}
            onClick={() => setSelectedBank(bank)}
          >
            <div className="flex items-center space-x-4">
              {/* Bank Logo */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md overflow-hidden flex-shrink-0"
                style={{ backgroundColor: bank.Color || "#10b981" }}
              >
                {bank.Image ? (
                  <Img
                    src={bank.Image}
                    alt={bank.Type}
                    className="w-8 h-8 object-contain"
                    ratio={1}
                  />
                ) : (
                  <i className="fas fa-university text-white text-lg"></i>
                )}
              </div>

              {/* Bank Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-0.5">
                  {bank.Type}
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm break-all mb-0.5">
                  {bank.BankNumber}
                </p>
                {bank.BankName && (
                  <p className="text-gray-500 text-xs">{bank.BankName}</p>
                )}
              </div>

              {/* Selection Indicator */}
              {selectedBank?.ID === bank.ID && (
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-check text-white text-xs"></i>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Step 2: Amount Input
  const renderStep2 = () => (
    <div className="space-y-4">
      {/* Balance Display */}
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-4">
        <div className="text-center">
          <p className="text-red-700 text-sm mb-1">{"ยอดเงินคงเหลือ"}</p>
          <p className="text-red-800 text-2xl font-bold">
            {CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
            {formatBalance()}
          </p>
        </div>
      </div>

      <label className="block text-gray-900 font-bold text-sm sm:text-base">
        {"จำนวนเงิน"} ({CurrencyName || "THB"})
      </label>

      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 font-bold text-base sm:text-lg">
          {CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
        </div>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={amount}
          onChange={(e) => handleAmountInputChange(e.target.value)}
          placeholder="0"
          className="w-full text-gray-700 pl-8 pr-4 py-3 sm:py-4 text-lg sm:text-xl font-bold bg-white border-2 border-gray-700 rounded-xl focus:border-red-500 focus:ring-0 outline-none transition-all duration-200"
          maxLength={12}
        />
      </div>

      {/* Quick Amount Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[500, 1000, 5000, 10000, 20000, 50000, 100000].map((preAmount) => (
          <button
            key={preAmount}
            className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 ${
              amount === preAmount.toString()
                ? "bg-red-600 text-white shadow-md transform scale-[1.02]"
                : "bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700 shadow-sm"
            }`}
            onClick={() => handleAmountSelect(preAmount)}
            disabled={preAmount > maxWithdrawAmountInteger}
          >
            {CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
            {preAmount.toLocaleString()}
          </button>
        ))}

        {/* ถอนทั้งหมด Button */}
        <button
          className={`py-2 px-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 col-span-2 sm:col-span-4 ${
            amount === maxWithdrawAmountInteger.toString()
              ? "bg-red-600 text-white shadow-md transform scale-[1.02]"
              : "bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 shadow-sm"
          }`}
          onClick={() => handleAmountSelect(maxWithdrawAmountInteger)}
          disabled={maxWithdrawAmountInteger <= 0}
        >
          {"ถอนทั้งหมด"} ({CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
          {maxWithdrawAmountInteger.toLocaleString()})
        </button>
      </div>

      {/* Amount validation */}
      {amount && parseInt(amount, 10) > maxWithdrawAmountInteger && (
        <p className="text-red-600 text-sm">{"จำนวนเงินเกินยอดคงเหลือ"}</p>
      )}
    </div>
  );

  // Step 3: Confirmation
  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Success Message */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-check text-red-600 text-2xl"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          {"แจ้งถอนเงินสำเร็จ"}
        </h3>
        <p className="text-gray-600 text-sm">
          {"ระบบได้รับรายการถอนเงินของท่านแล้ว"}
        </p>
      </div>

      {/* Transaction Details */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">{"หมายเลขอ้างอิง"}</span>
          <span className="font-mono text-sm font-bold">
            {withdrawResult?.reference_id}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">{"จำนวนเงิน"}</span>
          <span className="font-bold text-lg">
            {CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
            {withdrawResult?.amount?.toLocaleString()}
          </span>
        </div>

        {withdrawResult?.fee && withdrawResult.fee > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600 text-sm">{"ค่าธรรมเนียม"}</span>
            <span className="text-red-600 font-bold">
              -{CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
              {withdrawResult.fee.toLocaleString()}
            </span>
          </div>
        )}

        <div className="flex justify-between border-t pt-3">
          <span className="text-gray-600 text-sm">{"ยอดเงินที่ได้รับ"}</span>
          <span className="font-bold text-lg text-red-600">
            {CurrencyName === "THB" ? "฿" : CurrencyName || "฿"}
            {withdrawResult?.net_amount?.toLocaleString()}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">{"ธนาคาร"}</span>
          <span className="font-medium">{selectedBank?.Type}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">{"เลขบัญชี"}</span>
          <span className="font-mono text-sm">{selectedBank?.BankNumber}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600 text-sm">{"สถานะ"}</span>
          <span className="text-orange-600 font-medium">{"รอดำเนินการ"}</span>
        </div>
      </div>

      {/* Warning Message */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="fas fa-info-circle text-orange-600 text-sm"></i>
          </div>
          <div>
            <h4 className="text-orange-800 text-sm font-semibold mb-1">
              {"ข้อมูลสำคัญ"}
            </h4>
            <p className="text-orange-700 text-sm leading-relaxed">
              {
                "ระบบจะดำเนินการโอนเงินเข้าบัญชีธนาคารของท่านภายใน 24 ชั่วโมง โปรดตรวจสอบยอดเงินในบัญชีธนาคารของท่าน"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Get current step content
  const getCurrentStepContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <>
      {/* Modal Backdrop */}
      {isWithdrawV2Modal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-md flex items-start justify-center p-0 overflow-hidden animate-in fade-in duration-300"
          onClick={(e) => {
            if (e.target === e.currentTarget && !loading) {
              closeModal();
            }
          }}
          style={{
            zIndex: 9999,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          {/* Scrollable Container */}
          <div
            className="w-full h-full overflow-y-auto flex items-start justify-center p-4 sm:p-6 pt-20 pb-20 sm:pt-12 sm:pb-12"
            onClick={(e) => {
              if (e.target === e.currentTarget && !loading) {
                closeModal();
              }
            }}
          >
            {/* Modal Container */}
            <div
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl transform transition-all duration-500 ease-out flex flex-col h-auto max-h-[75vh] sm:max-h-[70vh] mt-4 sm:mt-0 animate-in slide-in-from-bottom-8 zoom-in-95 duration-700 ease-out"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {/* Close Button */}
              {!loading && (
                <button
                  onClick={() => closeModal()}
                  className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-all duration-200 hover:scale-110 active:scale-95 shadow-md border border-gray-200"
                  title={"ปิด"}
                >
                  <span className="text-lg font-bold leading-none">×</span>
                </button>
              )}

              {/* Modal Header */}
              <div className="bg-gradient-to-br from-red-50 to-white p-3 sm:p-6 rounded-t-3xl border-b border-gray-100 flex-shrink-0">
                <div className="animate-in slide-in-from-left-6 duration-600 delay-200">
                  <h3 className="text-base sm:text-xl font-bold text-gray-900">
                    {stepTitles[step]}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {"ขั้นตอนที่"} {step} {"จาก"} 3
                  </p>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-3 sm:p-6 flex-1 overflow-y-auto animate-in fade-in slide-in-from-bottom-6 duration-700 delay-300">
                {/* Step Indicator */}
                <div className="flex items-center justify-center mb-3 sm:mb-6">
                  {[1, 2, 3].map((s, index) => (
                    <React.Fragment key={s}>
                      <div
                        className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-300 ${
                          s <= step
                            ? "bg-red-600 text-white shadow-lg"
                            : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {s}
                      </div>
                      {index < 2 && (
                        <div
                          className={`w-8 sm:w-12 h-0.5 mx-1 transition-all duration-300 ${
                            s < step ? "bg-red-600" : "bg-gray-200"
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Content */}
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-600 delay-600">
                  {getCurrentStepContent()}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-3 sm:p-6 border-t border-gray-100 bg-white rounded-b-3xl flex-shrink-0 animate-in slide-in-from-bottom-6 duration-600 delay-500">
                <button
                  className={`w-full py-2.5 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-sm sm:text-base transition-all duration-200 ${
                    isButtonDisabled()
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                  }`}
                  onClick={handleNext}
                  disabled={isButtonDisabled()}
                >
                  {loading && <i className="fas fa-spinner fa-spin mr-2"></i>}
                  {getButtonText()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: AppReducerStateProps) => {
  return {
    state: state,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    closeModal: () => {
      dispatch({
        type: "ACTION_WITHDRAW_V2_IS_CLOSE_MODAL",
      });
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithdrawModalV2);
